/* ─────────────────────────────────────────────────────────────
   gpc-build-tabs.js
   Upload to: Assets/

   Behaviour for the product page build tabs:
     • the tab bar itself
     • the "how does this CPU compare" toggles
     • the rear I/O accordions
     • the Compare tab — this build pinned, up to two alternatives

   Loaded GLOBALLY from snippets/head.liquid, not from the section. Every
   product template used to inline its own copy of this; there were 8 slightly
   different versions across 20 templates, which is 8 chances for a fix to be
   applied to some builds and not others.

   Selection is shared with assets/gpc-compare.js through the same
   localStorage key, so builds ticked here arrive already ticked on
   /pages/compare, and vice versa.
   ───────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var KEY = 'gpcCompare';   // shared with gpc-compare.js — do not rename one without the other
  var MAX = 3;              // including the build you are on

  function readSel() {
    try {
      var v = JSON.parse(window.localStorage.getItem(KEY) || '[]');
      return Array.isArray(v) ? v : [];
    } catch (e) { return []; }
  }
  function writeSel(list) {
    try { window.localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX))); } catch (e) {}
  }
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function parseJSON(el, what) {
    if (!el) return null;
    try { return JSON.parse(el.textContent); }
    catch (e) {
      if (window.console) {
        console.error('[gpc-build-tabs] could not read the ' + (what || 'data') +
          ' payload — a product in the comparable collection has a metafield ' +
          'that is not valid JSON.', e);
      }
      return null;
    }
  }

  /* ── 1. the tab bar ─────────────────────────────────────── */
  function initTabs(root) {
    var links = [].slice.call(root.querySelectorAll('.pc-tab-link[data-panel]'));
    if (!links.length) return;

    function show(link) {
      links.forEach(function (l) {
        var panel = document.getElementById(l.getAttribute('data-panel'));
        var on = l === link;
        l.setAttribute('aria-expanded', String(on));
        if (panel) panel.hidden = !on;
        if (panel && on) wake(panel);
      });
    }

    /* A lazy image that was inside a display:none panel does not always start
       loading when the panel is revealed — mobile Safari in particular leaves it
       blank for good. On reveal, promote anything still lazy and nudge the src,
       which is what actually kicks off the fetch. Cheap, and it means a panel
       can keep using loading="lazy" without the risk. */
    function wake(panel) {
      panel.querySelectorAll('img[loading="lazy"]').forEach(function (im) {
        im.setAttribute('loading', 'eager');
        if (im.complete && im.naturalWidth > 0) return;
        var src = im.getAttribute('src');
        if (!src) return;
        im.removeAttribute('src');
        im.setAttribute('src', src);
      });
    }
    links.forEach(function (l) {
      l.addEventListener('click', function (e) { e.preventDefault(); show(l); });
    });

    // A link straight to #tab-compare opens that tab rather than the first one.
    var hash = window.location.hash.replace('#', '');
    var wanted = hash && links.filter(function (l) { return l.getAttribute('data-panel') === hash; })[0];
    show(wanted || links[0]);
  }

  /* ── 2. the CPU comparison toggles ──────────────────────── */
  function initToggles(root) {
    root.querySelectorAll('.pc-cpu-compare__toggle[aria-controls]').forEach(function (btn) {
      if (btn.dataset.pcReady === '1') return;
      btn.dataset.pcReady = '1';
      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel) return;
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', String(open));
        btn.textContent = open
          ? (btn.getAttribute('data-label-close') || 'Hide comparison')
          : (btn.getAttribute('data-label-open') || 'Show comparison');
        panel.hidden = !open;
      });
    });
  }

  /* ── 3. the rear I/O accordions ─────────────────────────── */
  function initIO(root) {
    root.querySelectorAll('.pc-io__group-header').forEach(function (head) {
      if (head.dataset.pcReady === '1') return;
      head.dataset.pcReady = '1';
      head.addEventListener('click', function () {
        var group = head.closest('.pc-io__group');
        var open = group.classList.contains('is-open');
        group.classList.toggle('is-open');
        head.setAttribute('aria-expanded', String(!open));
      });
    });
  }

  /* ── 4. the video, loaded on click ──────────────────────── */
  function initVideo(root) {
    root.querySelectorAll('[data-pcv]').forEach(function (el) {
      if (el.dataset.pcvReady === '1') return;
      el.dataset.pcvReady = '1';
      el.addEventListener('click', function () {
        var src = el.getAttribute('data-src');
        if (!src || el.classList.contains('is-playing')) return;
        var f = document.createElement('iframe');
        f.src = src;
        f.title = el.getAttribute('data-title') || 'Video';
        f.setAttribute('allow',
          'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
        f.setAttribute('allowfullscreen', '');
        f.setAttribute('frameborder', '0');
        // The poster goes, the player takes its place. Nothing from YouTube was
        // fetched before this click except the thumbnail.
        el.innerHTML = '';
        el.appendChild(f);
        el.classList.add('is-playing');
      });
    });
  }

  /* ── 5. the Compare tab ─────────────────────────────────── */
  var ICONS = {
    cpu: '<rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><path d="M9 1v3M15 1v3M9 20v3M15 20v3M20 9h3M20 14h3M1 9h3M1 14h3"/>',
    gpu: '<rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="8" cy="12" r="2.6"/><circle cx="16" cy="12" r="2.6"/>',
    ram: '<rect x="2" y="7" width="20" height="10" rx="1.5"/><path d="M6 17v3M10 17v3M14 17v3M18 17v3M7 11h3M14 11h3"/>',
    ssd: '<rect x="2" y="9" width="20" height="7" rx="2"/><path d="M6 12.5h.01M10 12.5h.01"/><path d="M5 9V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v3"/>',
    cooler: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="2"/><path d="M12 3v7M12 14v7M3 12h7M14 12h7"/>',
    mobo: '<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M8 3v4M8 7h4v4"/><circle cx="16" cy="8" r="1.6"/><circle cx="8" cy="16" r="1.6"/><path d="M12 16h5"/>',
    'case': '<rect x="5" y="2" width="14" height="20" rx="2"/><path d="M9 6h6M9 10h6"/><circle cx="12" cy="16" r="2.2"/>',
    colour: '<path d="M12 2.7s6 6.6 6 10.4a6 6 0 0 1-12 0c0-3.8 6-10.4 6-10.4z"/>',
    dims: '<rect x="2" y="8" width="20" height="8" rx="1.6"/><path d="M6.5 8v3M10 8v4M13.5 8v3M17 8v4"/>',
    psu: '<path d="M18.36 6.64a9 9 0 1 1-12.73 0"/><path d="M12 2v10"/>',
    game: '<rect x="2" y="7" width="20" height="11" rx="4"/><path d="M7 12h3M8.5 10.5v3"/><circle cx="15.5" cy="11.5" r="1"/><circle cx="17.5" cy="14" r="1"/>'
  };
  function icon(k) {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + (ICONS[k] || ICONS.cpu) + '</svg>';
  }
  var RES = [{ k: '1080p', l: '1080p' }, { k: '1440p', l: '1440p' }, { k: '4k', l: '4K' }];

  function initCompare(root) {
    if (root.dataset.pccReady === '1') return;
    root.dataset.pccReady = '1';

    var all = parseJSON(root.querySelector('[data-pcc-data]'), 'builds') || [];
    var rows = parseJSON(root.querySelector('[data-pcc-rows]'), 'rows') || [];
    var mount = root.querySelector('[data-pcc-table]');
    if (!mount) return;

    var byHandle = {};
    all.forEach(function (p) { byHandle[p.h] = p; });

    // This build is already in the payload — look it up rather than have the
    // section print a second copy of the same product.
    var self = byHandle[root.dataset.selfHandle];
    if (!self) {
      if (window.console) {
        console.warn('[gpc-build-tabs] "' + root.dataset.selfHandle + '" is not in the ' +
          'comparable collection, so there is nothing to compare it against.');
      }
      return;
    }

    var toggle = root.querySelector('[data-pcc-diffs]');
    var full = root.querySelector('[data-pcc-full]');
    var comparePath = root.dataset.comparePath || '/pages/compare';
    var addLabel = root.dataset.addLabel || 'Add a build';
    var addPlaceholder = root.dataset.addPlaceholder || 'Search builds';
    var prefill = root.dataset.prefill === 'true';

    // Two slots. Pre-filled from whatever the shopper already ticked elsewhere
    // on the site, minus this build — it is pinned and cannot be a slot.
    var picked = [null, null];
    if (prefill) {
      readSel().filter(function (h) { return h !== self.h && byHandle[h]; })
        .slice(0, 2).forEach(function (h, i) { picked[i] = byHandle[h]; });
    }

    var chosenGpu = {};

    function ids() {
      return [self.h].concat(picked.filter(Boolean).map(function (p) { return p.h; }));
    }
    function remember() {
      writeSel(ids());
      if (full) full.setAttribute('href', comparePath + '?ids=' + encodeURIComponent(ids().join(',')));
    }

    function val(main, sub) {
      if (!main && !sub) return '';
      return esc(main || '') + (sub ? '<span class="pcc-sub">' + esc(sub) + '</span>' : '');
    }
    function fmt(p, row) {
      if (!p) return '';                      // an empty slot is not a build with missing data
      var s = p.specs || {};
      switch (row.k) {
        case 'gpu':
          return (p.gpu || []).map(function (g) {
            return '<span class="pcc-chip">' + esc(g) + '</span>'; }).join('');
        case 'ram': return val(p.ramOpt, s.ram);
        case 'ssd': return val(p.ssdOpt, s.ssd);
        case 'colour':
          return s.colour ? '<span class="pcc-swatch"><i style="background:' +
            esc(s.colourHex || '#888') + '"></i>' + esc(s.colour) + '</span>' : '';
        case 'dims': return val(s.dims, s.dimsNote);
        default: return val(s[row.k], '');
      }
    }
    function benchFor(p, game, res) {
      var g = (p.bench || {})[chosenGpu[p.h]] || {};
      var v = (g[game] || {})[res];
      return (typeof v === 'number' && v > 0) ? v : null;
    }
    function gamesShown(list) {
      var out = [];
      list.forEach(function (p) {
        Object.keys(p.bench || {}).forEach(function (gpu) {
          Object.keys(p.bench[gpu] || {}).forEach(function (game) {
            if (out.indexOf(game) === -1) out.push(game);
          });
        });
      });
      return out.filter(function (game) {
        return list.some(function (p) {
          return RES.some(function (r) { return benchFor(p, game, r.k); });
        });
      });
    }

    function slotHTML(i) {
      return '<div class="pcc-slot">' +
        '<span class="pcc-plus" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
        'stroke-width="2.4" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg></span>' +
        '<b>' + esc(addLabel) + '</b>' +
        '<input type="search" data-pcc-search="' + i + '" placeholder="' + esc(addPlaceholder) + '" ' +
        'aria-label="' + esc(addPlaceholder) + '">' +
        '<ul class="pcc-results" data-pcc-results="' + i + '"></ul></div>';
    }
    function headHTML(p, i, pinned) {
      // A build with no photo still gets the tile. Dropping the element made the
      // column shorter than its neighbours and the header rows stopped lining up.
      //
      // NOT loading="lazy". This table is built while the Compare panel is still
      // hidden, and mobile Safari will not load a lazy image that was created
      // inside a display:none container — not even once the panel is shown. It
      // is why the photos appeared on desktop and not on a phone. There are
      // never more than three of them, so eager costs nothing.
      return (pinned ? '<span class="pcc-thisbuild">This build</span>' : '') +
        '<span class="pcc-shot">' +
          (p.img ? '<img src="' + esc(p.img) + '" alt="">' : '') +
        '</span>' +
        '<a class="pcc-name" href="' + esc(p.u) + '">' + esc(p.t) + '</a>' +
        '<span class="pcc-price"><em>From</em>' + esc(p.p) + '</span>' +
        (pinned
          ? '<span class="pcc-locked"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
            'stroke-width="2.4"><rect x="4" y="11" width="16" height="10" rx="2"/>' +
            '<path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>Always shown</span>'
          : '<button type="button" class="pcc-remove" data-pcc-remove="' + i + '">Remove</button>');
    }

    function render() {
      var live = [self].concat(picked.filter(Boolean));
      live.forEach(function (p) {
        if (!chosenGpu[p.h]) {
          var keys = Object.keys(p.bench || {});
          chosenGpu[p.h] = keys.length ? keys[0] : (p.gpu || [])[0] || '';
        }
      });

      var html = '<table class="pcc-table"><colgroup><col class="pcc-labcol">' +
        '<col style="width:24%"><col class="pcc-gapcol"><col style="width:24%"><col style="width:24%">' +
        '</colgroup><thead><tr><th></th>' +
        '<th class="pcc-pin">' + headHTML(self, -1, true) + '</th>' +
        '<th class="pcc-gap"></th>' +
        [0, 1].map(function (i) {
          return picked[i]
            ? '<th>' + headHTML(picked[i], i, false) + '</th>'
            : '<th class="pcc-slotcell">' + slotHTML(i) + '</th>';
        }).join('') +
        '</tr></thead><tbody>';

      rows.forEach(function (row) {
        var cells = [self, picked[0], picked[1]].map(function (p) { return fmt(p, row); });
        if (!cells[0] && !cells[1] && !cells[2]) return;           // nobody has it
        var live2 = cells.filter(function (c, ix) { return ix === 0 || picked[ix - 1]; });
        var same = live2.length > 1 && live2.every(function (c) { return c === live2[0]; });
        html += '<tr class="pcc-row' + (same ? ' is-same' : '') + '" data-same="' + (same ? '1' : '0') + '">' +
          '<th scope="row"><span class="pcc-lab">' + icon(row.k) + '<b>' + esc(row.label) + '</b></span></th>' +
          '<td class="pcc-pin">' + (cells[0] || '<span class="pcc-dash">&mdash;</span>') + '</td>' +
          '<td class="pcc-gap"></td>' +
          '<td>' + (picked[0] ? (cells[1] || '<span class="pcc-dash">&mdash;</span>') : '') + '</td>' +
          '<td>' + (picked[1] ? (cells[2] || '<span class="pcc-dash">&mdash;</span>') : '') + '</td></tr>';
      });

      var games = gamesShown(live);
      if (games.length) {
        html += '<tr class="pcc-grp"><td colspan="2"><span>Performance</span>' +
          '<small>Average FPS on the graphics card each build ships with.</small></td>' +
          '<td class="pcc-gap"></td><td colspan="2"></td></tr>';
        games.forEach(function (game) {
          var label = game.charAt(0).toUpperCase() + game.slice(1);
          html += '<tr class="pcc-row"><th scope="row"><span class="pcc-lab">' + icon('game') +
            '<b>' + esc(label) + '</b></span></th>';
          [self, picked[0], picked[1]].forEach(function (p, ix) {
            if (ix === 1) html += '<td class="pcc-gap"></td>';
            if (!p) { html += '<td></td>'; return; }
            html += '<td><span class="pcc-fps">' + RES.map(function (r) {
              var v = benchFor(p, game, r.k);
              return '<span class="pcc-fpscell"><span class="pcc-res">' + r.l + '</span>' +
                (v ? '<b>' + v + '<s>fps</s></b>' : '<span class="pcc-dash">&mdash;</span>') + '</span>';
            }).join('') + '</span></td>';
          });
          html += '</tr>';
        });
      }

      mount.innerHTML = html + '</tbody></table>';
      applyDiffs();
      remember();
    }

    function applyDiffs() {
      var on = toggle && toggle.checked;
      mount.querySelectorAll('[data-same="1"]').forEach(function (tr) {
        tr.classList.toggle('is-hidden', !!on);
      });
    }

    root.addEventListener('input', function (e) {
      var i = e.target.getAttribute && e.target.getAttribute('data-pcc-search');
      if (i === null || i === undefined) return;
      var q = e.target.value.trim().toLowerCase();
      var ul = root.querySelector('[data-pcc-results="' + i + '"]');
      if (!ul) return;
      if (!q) { ul.innerHTML = ''; return; }
      var taken = ids();
      var hits = all.filter(function (p) {
        if (taken.indexOf(p.h) > -1) return false;
        var hay = (p.h + ' ' + p.t + ' ' + ((p.specs || {}).cpu || '') + ' ' + (p.gpu || []).join(' ')).toLowerCase();
        return hay.indexOf(q) > -1;
      }).slice(0, 5);
      ul.innerHTML = hits.length
        ? hits.map(function (p) {
            return '<li><button type="button" data-pcc-add="' + esc(p.h) + '" data-into="' + i + '">' +
              (p.img ? '<img src="' + esc(p.img) + '" alt="">' : '<span class="pcc-noimg"></span>') +
              '<span><b>' + esc(p.t) + '</b><em>' + esc(p.p) + '</em></span></button></li>';
          }).join('')
        : '<li class="pcc-noresults">No builds found</li>';
    });

    root.addEventListener('click', function (e) {
      var add = e.target.closest && e.target.closest('[data-pcc-add]');
      if (add) {
        picked[+add.getAttribute('data-into')] = byHandle[add.getAttribute('data-pcc-add')] || null;
        render();
        return;
      }
      var rm = e.target.closest && e.target.closest('[data-pcc-remove]');
      if (rm) { picked[+rm.getAttribute('data-pcc-remove')] = null; render(); }
    });

    if (toggle) toggle.addEventListener('change', applyDiffs);
    render();
  }

  /* ── boot ───────────────────────────────────────────────── */
  function initAll(scope) {
    (scope || document).querySelectorAll('.pc-build-tabs').forEach(function (el) {
      initTabs(el); initToggles(el); initIO(el); initVideo(el);
    });
    (scope || document).querySelectorAll('[data-pcc-root]').forEach(initCompare);
  }

  window.GPC_TABS_VERSION = '2026-08-16 build tabs v4 (mobile images)';

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
